import type { Persona, PrototypeBootstrap } from "@peer-start/platform-contracts";
import { PanelLeftClose, PanelLeftOpen, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { matchPath, NavLink } from "react-router-dom";
import type { Workspace } from "../types/shell.js";
import { useSituationCard } from "../../domains/administration/api/situation-card-queries.js";
import { ProfileMenu } from "./ProfileMenu.js";
import ewiseLogo from "../../assets/e-wise-logo.png";
import { isMentor, workspaceHome } from "../../domains/prototype/data/user-management.js";
import { ShellBreadcrumbs } from "./ShellBreadcrumbs.js";
import { breadcrumbsFor } from "./shell-breadcrumbs.js";

interface AppTopbarProps {
  bootstrap: PrototypeBootstrap;
  sidebarExpanded: boolean;
  persona: Persona;
  onPersonaChange: (id: string) => void;
  onSidebarToggle: () => void;
  pathname: string;
  search?: string;
  workspace: Workspace;
}

export function AppTopbar({
  bootstrap,
  sidebarExpanded,
  persona,
  onPersonaChange,
  onSidebarToggle,
  pathname,
  search = "",
  workspace,
}: AppTopbarProps) {
  const { t } = useTranslation();
  const home = workspaceHome(persona);
  const brand = workspace === "admin" ? t("common.digitalColleague") : t("common.appName");
  const cardList = matchPath({ path: "/admin/situation-cards", end: true }, pathname) !== null;
  const cardId =
    matchPath({ path: "/admin/situation-cards/:cardId", end: true }, pathname)?.params.cardId ?? "";
  const card = useSituationCard(cardId);
  const breadcrumbs = breadcrumbsFor(pathname, workspace, search, isMentor(persona)).map((crumb) =>
    crumb.labelKey === "breadcrumbs.situationCard" && card.data
      ? { ...crumb, label: card.data.title }
      : crumb,
  );
  return (
    <header className="z-30 flex h-[64px] shrink-0 items-center gap-3 border-b border-border bg-background px-4">
      <NavLink
        aria-label={brand}
        className="flex items-center gap-2 text-sm font-medium md:hidden"
        data-control-id="shell.logo"
        to={home}
      >
        {workspace === "admin" ? (
          <img alt="" className="size-8 shrink-0 rounded-md object-cover" src={ewiseLogo} />
        ) : (
          <span className="grid size-8 place-items-center rounded-md bg-primary text-primary-foreground shadow-xs">
            <Sparkles aria-hidden="true" className="size-4" />
          </span>
        )}
        {brand}
      </NavLink>
      {cardId ? null : (
        <button
          aria-expanded={sidebarExpanded}
          aria-label={t(sidebarExpanded ? "nav.collapseMenu" : "nav.expandMenu")}
          className="hidden size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25 md:grid"
          data-control-id="shell.sidebar.toggle"
          onClick={onSidebarToggle}
          type="button"
        >
          {sidebarExpanded ? (
            <PanelLeftClose aria-hidden="true" className="size-4" />
          ) : (
            <PanelLeftOpen aria-hidden="true" className="size-4" />
          )}
        </button>
      )}
      {cardList ? null : (
        <>
          {cardId ? null : <span aria-hidden="true" className="hidden h-5 w-px bg-border md:block" />}
          {cardId ? (
            <div className="flex min-w-0 flex-1 items-center" id="situation-card-toolbar" />
          ) : (
            <ShellBreadcrumbs breadcrumbs={breadcrumbs} />
          )}
        </>
      )}
      <div className="ml-auto flex min-w-0 items-center gap-2">
        <div className="flex items-center gap-2" id="learning-route-toolbar" />
        <ProfileMenu
          className="shrink-0 md:hidden"
          onPersonaChange={onPersonaChange}
          persona={persona}
          personas={bootstrap.personas}
          placement="header"
        />
      </div>
    </header>
  );
}
